import { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import MeetingDetail from "@/components/MeetingDetail";
import {
  X, Plus, CalendarPlus, VideoCamera, Clock, Users, Sparkle, CaretRight, ArrowLeft, Play, Trash,
} from "@phosphor-icons/react";

const inputCls = "w-full h-11 px-3 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary text-sm";
const labelCls = "text-xs font-bold uppercase tracking-[0.12em] text-black/60 mb-1 block";

const STATUS_META = {
  scheduled: { label: "Scheduled", bg: "#E0F2FE" },
  in_progress: { label: "Live", bg: "#DCFCE7" },
  ended: { label: "Ended", bg: "#F3F4F6" },
  cancelled: { label: "Cancelled", bg: "#FEE2E2" },
};

function fmtDate(iso) {
  if (!iso) return "No date";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Pacific/Honolulu" });
}

export default function MeetingsPanel({ open, onClose, onChanged, onGenerate, onOpenSettings }) {
  const { user } = useAuth();
  const [view, setView] = useState("list"); // list | create
  const [tab, setTab] = useState("upcoming");
  const [meetings, setMeetings] = useState([]);
  const [users, setUsers] = useState([]);
  const [detailId, setDetailId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [instant, setInstant] = useState(false);
  const [autoRecord, setAutoRecord] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", scheduled_at: "", duration_minutes: 30, attendee_ids: [], ai_summary_enabled: true });

  const load = useCallback(async () => {
    const { data } = await api.get("/meetings", { params: { filter: tab } });
    setMeetings(data);
  }, [tab]);

  useEffect(() => {
    if (open) {
      load();
      api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    }
  }, [open, load]);

  if (!open) return null;

  const localNow = () => {
    const d = new Date(Date.now() - new Date().getTimezoneOffset() * 60000);
    return d.toISOString().slice(0, 16);
  };

  const blank = (extra) => ({ title: "", description: "", scheduled_at: "", duration_minutes: 30, attendee_ids: [], ai_summary_enabled: true, ...extra });
  const openScheduled = () => { setInstant(false); setForm(blank()); setView("create"); };
  const openInstant = () => { setInstant(true); setForm(blank({ scheduled_at: localNow() })); setView("create"); };

  const toggleAttendee = (id) =>
    setForm((f) => ({ ...f, attendee_ids: f.attendee_ids.includes(id) ? f.attendee_ids.filter((x) => x !== id) : [...f.attendee_ids, id] }));

  const removeMeeting = async (e, m) => {
    e.stopPropagation();
    if (!window.confirm(`Delete meeting "${m.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/meetings/${m.id}`); toast.success("Meeting deleted"); load(); onChanged?.(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Meeting title is required"); return; }
    setBusy(true);
    try {
      const payload = { ...form, scheduled_at: form.scheduled_at ? new Date(form.scheduled_at).toISOString() : new Date().toISOString() };
      const { data } = await api.post("/meetings", payload);
      if (instant) {
        await api.post(`/meetings/${data.id}/start`);
        toast.success(form.ai_summary_enabled ? "AI meeting started — recording" : "Meeting started");
      } else {
        toast.success("Meeting scheduled");
      }
      setForm(blank());
      setView("list"); setTab(instant ? "all" : "upcoming"); load(); onChanged?.();
      if (instant) { setAutoRecord(form.ai_summary_enabled); setDetailId(data.id); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div data-testid="meetings-panel" onClick={(e) => e.stopPropagation()} className="relative bg-background border-l-2 border-black w-full max-w-lg h-full overflow-y-auto">
        <div className="sticky top-0 bg-[#DCFCE7] border-b-2 border-black px-5 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            {view === "create" && <button data-testid="meetings-back-btn" onClick={() => setView("list")} className="press w-8 h-8 rounded-full border-2 border-black flex items-center justify-center bg-white"><ArrowLeft size={16} weight="bold" /></button>}
            <div className="w-9 h-9 rounded-lg bg-secondary border-2 border-black flex items-center justify-center"><VideoCamera size={18} weight="fill" color="#fff" /></div>
            <h2 className="font-display text-xl font-bold">{view === "create" ? (instant ? "Start Meeting Now" : "New Meeting") : "Meetings"}</h2>
          </div>
          <button data-testid="meetings-close-btn" onClick={onClose} className="press w-9 h-9 rounded-full border-2 border-black flex items-center justify-center bg-white"><X size={16} weight="bold" /></button>
        </div>

        {view === "list" ? (
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <button data-testid="meeting-start-now-btn" onClick={openInstant} className="press h-12 rounded-full bg-secondary text-white font-bold border-2 border-black hard-shadow-sm flex items-center justify-center gap-2">
                <Play size={18} weight="fill" /> Start now
              </button>
              <button data-testid="meeting-new-btn" onClick={openScheduled} className="press h-12 rounded-full bg-primary text-white font-bold border-2 border-black hard-shadow-sm flex items-center justify-center gap-2">
                <CalendarPlus size={18} weight="bold" /> Schedule
              </button>
            </div>

            <div className="flex gap-2">
              {["upcoming", "past", "all"].map((t) => (
                <button key={t} data-testid={`meetings-tab-${t}`} onClick={() => setTab(t)} className={`press h-9 px-4 rounded-full text-sm font-bold border-2 border-black capitalize ${tab === t ? "bg-secondary text-white" : "bg-white"}`}>{t}</button>
              ))}
            </div>

            {meetings.length === 0 ? (
              <div className="border-2 border-dashed border-black/30 rounded-xl p-10 text-center">
                <VideoCamera size={36} className="mx-auto text-black/30" />
                <p className="font-display text-lg font-bold mt-2">No meetings</p>
                <p className="text-sm text-black/50">Schedule your first meeting to get started.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {meetings.map((m) => {
                  const sm = STATUS_META[m.status] || STATUS_META.scheduled;
                  return (
                    <button key={m.id} data-testid={`meeting-item-${m.id}`} onClick={() => setDetailId(m.id)} className="press w-full text-left rounded-xl border-2 border-black bg-white p-4 hard-shadow-sm flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border-2 border-black" style={{ backgroundColor: sm.bg }}>{sm.label}</span>
                          {m.ai_summary_enabled && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border-2 border-black bg-[#F3E8FF] flex items-center gap-1"><Sparkle size={10} weight="fill" /> AI</span>}
                        </div>
                        <p className="font-bold truncate">{m.title}</p>
                        <div className="flex items-center gap-3 text-xs text-black/50 mt-1">
                          <span className="flex items-center gap-1"><Clock size={12} /> {fmtDate(m.scheduled_at)}</span>
                          <span className="flex items-center gap-1"><Users size={12} /> {(m.attendee_ids?.length || 0) + 1}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <span onClick={(e) => removeMeeting(e, m)} data-testid={`meeting-delete-${m.id}`} role="button" className="press w-8 h-8 rounded-full border-2 border-black bg-white flex items-center justify-center"><Trash size={13} weight="bold" /></span>
                        <CaretRight size={18} weight="bold" className="text-black/40" />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div>
              <label className={labelCls}>Title</label>
              <input data-testid="meeting-title-input" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Weekly team standup" />
            </div>
            <div>
              <label className={labelCls}>Description</label>
              <textarea data-testid="meeting-desc-input" className={inputCls + " h-24 py-2 resize-none"} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Agenda / purpose" />
            </div>
            <label data-testid="meeting-ai-enable-toggle" className="flex items-start gap-3 p-3 rounded-xl border-2 border-black bg-[#F3E8FF] cursor-pointer">
              <input type="checkbox" className="w-5 h-5 mt-0.5 shrink-0" checked={form.ai_summary_enabled} onChange={(e) => setForm({ ...form, ai_summary_enabled: e.target.checked })} />
              <span>
                <span className="font-bold text-sm flex items-center gap-1.5"><Sparkle size={15} weight="fill" /> Enable AI Meeting Assist</span>
                <span className="text-xs text-black/60 block mt-0.5">{instant ? "Starts recording right away and lets AI summarize, extract decisions & action items." : "Records the meeting and lets AI generate a summary you review before sharing."}</span>
              </span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Date & time</label>
                <input data-testid="meeting-date-input" type="datetime-local" className={inputCls} value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Duration (min)</label>
                <input data-testid="meeting-duration-input" type="number" min="5" step="5" className={inputCls} value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 30 })} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Attendees</label>
              <div className="grid grid-cols-2 gap-2">
                {users.filter((u) => u.id !== user.id).map((u) => (
                  <button key={u.id} type="button" data-testid={`meeting-attendee-${u.id}`} onClick={() => toggleAttendee(u.id)} className={`press text-left px-3 py-2 rounded-lg border-2 border-black text-sm font-bold ${form.attendee_ids.includes(u.id) ? "bg-primary text-white" : "bg-white"}`}>
                    {u.name}
                  </button>
                ))}
              </div>
            </div>
            <button data-testid="meeting-create-submit" onClick={submit} disabled={busy} className="press w-full h-12 rounded-full bg-primary text-white font-bold border-2 border-black hard-shadow-sm disabled:opacity-60 flex items-center justify-center gap-2">
              {instant ? <Play size={18} weight="fill" /> : <Plus size={18} weight="bold" />} {busy ? (instant ? "Starting…" : "Scheduling…") : (instant ? (form.ai_summary_enabled ? "Start AI meeting now" : "Start meeting now") : "Schedule meeting")}
            </button>
          </div>
        )}
      </div>

      {detailId && <MeetingDetail meetingId={detailId} autoRecord={autoRecord} onClose={() => { setDetailId(null); setAutoRecord(false); load(); }} onChanged={() => { load(); onChanged?.(); }} onGenerate={onGenerate} onOpenSettings={onOpenSettings} />}
    </div>
  );
}
